import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  checkDiscountApi,
  checkDiscountNonAuthApi,
  checkPriceApi,
  createNewOrderApi,
  getAllServicedDataApi,
  getS3SignUrl,
  getStaffsApi,
  getStaffsGenRegApi,
} from "../../services/api/ordersApis";
import { clearDatahandling } from "../../utils/clearDataHandling";
import { setLoadingContent, startLoading, stopLoading } from "./loadingSlice";
import { makePaymentForOrder } from "../../services/api/paymentApi";
import {
  loadingContentForCreateJob,
  loadingContentForStaffAvailability,
} from "../../utils/constants";

export const DISCOUNT_EXCEEDS_SERVICE_FEE_MESSAGE =
  "Discount exceeds or equals to service fee. Service fee must be greater than discount to apply this code.";
export const PRICE_NOT_LOADED_MESSAGE =
  "Service fee is not available yet. Please wait for the price to load, then apply your promo code.";
const INVALID_DISCOUNT_MESSAGE =
  "This promo code could not be applied. Please check the code and try again.";

const getDiscountAmountFromResult = (discountResult) => {
  const raw =
    discountResult?.discountAmount ??
    discountResult?.amount;
  return parseFloat(Number(raw).toFixed(2));
};

const normalizeDiscountResult = (discountResult) => {
  if (!discountResult) return null;
  return {
    ...discountResult,
    discountId: discountResult.discountId ?? discountResult._id ?? "",
    discountCode: discountResult.discountCode ?? "",
    discountAmount: getDiscountAmountFromResult(discountResult),
    discountType: discountResult.discountType ?? "",
  };
};

const validateDiscountCode = (discountResult, subtotal) => {
  if (!discountResult) {
    return { valid: false, message: INVALID_DISCOUNT_MESSAGE };
  }
  if (subtotal == null || !Number.isFinite(Number(subtotal))) {
    return { valid: false, message: PRICE_NOT_LOADED_MESSAGE };
  }

  const discountAmount = getDiscountAmountFromResult(discountResult);
  const serviceSubtotal = parseFloat(Number(subtotal).toFixed(2));

  if (discountResult.discountType === "flat") {
    if (!(discountAmount > 0)) {
      return { valid: false, message: INVALID_DISCOUNT_MESSAGE };
    }
    // Must leave a remaining service fee (no 100% / free orders)
    return discountAmount < serviceSubtotal
      ? { valid: true }
      : { valid: false, message: DISCOUNT_EXCEEDS_SERVICE_FEE_MESSAGE };
  }

  if (discountResult.discountType === "percentage") {
    // 100% discounts are not allowed
    if (!(discountAmount > 0 && discountAmount < 100)) {
      return discountAmount >= 100
        ? { valid: false, message: DISCOUNT_EXCEEDS_SERVICE_FEE_MESSAGE }
        : { valid: false, message: INVALID_DISCOUNT_MESSAGE };
    }
    return { valid: true };
  }

  return { valid: false, message: INVALID_DISCOUNT_MESSAGE };
};

const initialState = {
  isLoading: false,
  data: [],
  total: 1,
  params: {},
  allData: [],
  dataCount: 0,
  dataPerPage: 0,
  pageNumber: 0,
  selectedOrder: {},
  dataRange: null,
  customerList: [],
  // Order Post states starts from here
  selectedCustomer: null,
  selectedAddress: null,
  addressCheckedFromStore: false,
  selectedCategory: null,
  selectedCategoryOption: null,
  selectedDiscount: null,
  selectedService: null,
  selectedDuration: null,
  selectedStartDate: null,
  selectedEndDate: null,
  priceDetails: null,
  additionalComments: null,
  recurrenceRule: {},
  numberOfsessions: 0,
  frequencyShow: 0,
  regularServiceArrays: [],
  analysisArray: [],
  compalsaryMediaUpload: [],
  // Preferred staffs for regular order
  preferredStaffs: [],
  selectedPreferredStaff: [],
  // extra states for professional orders
  selectedProfServiceArray: [],
  profServiceArray: [],
  preferredDates: [],
  hasStaffAvailable: false,
  staffAvailabilityArrayForRegular: [],
  professionalServiceAllList: [],
  subOptionData: null,
  professionalServiceAllFiles: [],
  professionalServiceAllFilesForUiShow: [],
  dayNightOptionsForUi: [],
  timeRangeSelectionForDropDown: [],
  selectedTimeRangeForUIShow: null,
  serviceTypes: [],
  secondaryStaff: "",
};

export const checkPrice = createAsyncThunk(
  "checkprice",
  async (values, { rejectWithValue }) => {
    try {
      const data = await checkPriceApi(values);
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const getStaffs = createAsyncThunk(
  "getstaffs",
  async (values, { rejectWithValue }) => {
    values?.dispatch(setLoadingContent(loadingContentForStaffAvailability));
    try {
      const data = await getStaffsApi(values);
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      values?.dispatch(setLoadingContent([]));
      return data;
    } catch (error) {
      values?.dispatch(setLoadingContent([]));
      return rejectWithValue(error.response.data);
    }
  }
);
export const getStaffsGenReg = createAsyncThunk(
  "getStaffsGenReg",
  async (values, { rejectWithValue }) => {
    try {
      const data = await getStaffsGenRegApi(values);
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      values?.dispatch(setLoadingContent([]));
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDiscount = createAsyncThunk(
  "checkDiscount",
  async (values, { rejectWithValue }) => {
    try {
      const data = await checkDiscountApi(values);
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const checkDiscountNonLogin = createAsyncThunk(
  "checkDiscountNonLogin",
  async (values, { rejectWithValue }) => {
    try {
      const data = await checkDiscountNonAuthApi(values);
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

function blobToBase64(blob) {
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}
export const fileSelection = createAsyncThunk(
  "fileSelectionForProfessional",
  async (values, { rejectWithValue }) => {
    try {
      const data = await getS3SignUrl(values?.dataFiles);
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      if (data?.serverResponse?.code === 200) {
        values?.dispatch(
          setProfessionalServiceAllFilesForUiShow({
            file: await blobToBase64(values?.fileLink?.fileData),
            type: values?.fileLink?.fileData?.type,
            name: values?.fileLink?.fileData?.name,
            serviceId: values?.fileLink?.serviceId,
            link: data?.result,
          })
        );
      }
      return { data: data, serviceId: values?.serviceId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createNewOrder = createAsyncThunk(
  "createNewOrder",
  async (values, { rejectWithValue }) => {
    try {
      values?.dispatch(setLoadingContent(loadingContentForCreateJob));
      const data = await createNewOrderApi(values);
      if (data?.serverResponse?.code !== 201) return rejectWithValue(data);
      if (data?.serverResponse?.code === 201) {
        values?.dispatch(setLoadingContent(loadingContentForCreateJob));
        // Keep the full-screen loader up (independent of the per-request http
        // interceptor flag) so clearing the order state + initiating payment
        // never flashes the now-empty checkout/address before the HitPay redirect.
        values?.dispatch(startLoading());
        clearDatahandling(values?.dispatch);
        const createdOrder = data?.result?.newOrder;
        const skipPayment =
          createdOrder?.noPayment === true ||
          createdOrder?.status?.toLowerCase() === "paid";
        if (
          (createdOrder?.orderType?.toLowerCase() === "general" ||
            createdOrder?.orderType?.toLowerCase() === "regular") &&
          !skipPayment
        ) {
          window.history.pushState(
            null,
            "",
            `/order-details/${createdOrder?._id}`
          );
          // values.navigate(`/order-details/${data?.result?.newOrder?._id}`)
          let bodyData = {
            amount: createdOrder?.invoiceAmount,
            description: "",
            redirect_url: `${window.location.origin}/order-details/${createdOrder?._id}`,
            email: values?.user?.email,
            customerUserId: createdOrder?.createdFor,
            order_id: createdOrder?._id,
            paymentType: "Immediate Payment",
          };
          const payment = await makePaymentForOrder(bodyData);
          const paymentLink =
            payment?.result?.paymentLink ?? payment?.result?.data?.paymentLink;
          // makePaymentForOrder redirects via window.location on success; if no
          // link came back the redirect never happened, so release the loader
          // and surface the error instead of getting stuck behind the overlay.
          if (!paymentLink) {
            values?.dispatch(stopLoading());
            values?.dispatch(setLoadingContent([]));
            toast.error(
              payment?.serverResponse?.message ??
                payment?.message ??
                "Unable to connect to the payment gateway. Please try again."
            );
          }
        } else {
          values?.dispatch(stopLoading());
          values?.dispatch(setLoadingContent([]));
          values.navigate(`/order-thank-you`);
          toast.success(
            skipPayment && createdOrder?.noPayment
              ? "Order created successfully. No payment required."
              : data?.serverResponse?.message
          );
        }
        return data;
      }
    } catch (error) {
      values?.dispatch(stopLoading());
      values?.dispatch(setLoadingContent([]));
      return rejectWithValue(error.response.data);
    }
  }
);

// Get all data related to service creation for professional

export const getAllServicedData = createAsyncThunk(
  "getAllServicedData",
  async (values, { rejectWithValue }) => {
    try {
      const data = await getAllServicedDataApi();
      if (data?.serverResponse?.code !== 200) return rejectWithValue(data);
      if (data?.serverResponse?.code === 200) {
        return data;
      }
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createOrderSlice = createSlice({
  name: "createOrders",
  initialState,
  reducers: {
    setAddressCheckedFromStore: (state, action) => {
      const { addressCheckedFromStore } = action.payload;
      state.addressCheckedFromStore = addressCheckedFromStore;
    },
    setSelectAddress: (state, action) => {
      const { selectedAddress } = action.payload;
      state.selectedAddress = selectedAddress;
    },
    setSelectedOrder: (state, action) => {
      const { selectedOrder } = action.payload;
      state.selectedOrder = selectedOrder;
    },
    setSelectedDiscount: (state, action) => {
      const { selectedDiscount } = action.payload;
      state.selectedDiscount = selectedDiscount;
    },
    setSelectCategory: (state, action) => {
      const { selectedCategory } = action.payload;
      state.selectedCategory = selectedCategory;
    },
    setSelectCategoryOptions: (state, action) => {
      const { selectedCategoryOption } = action.payload;
      state.selectedCategoryOption = selectedCategoryOption;
    },
    setSelectedService: (state, action) => {
      const { selectedService } = action.payload;
      state.selectedService = selectedService;
    },
    setSelectedDuration: (state, action) => {
      const { selectedDuration } = action.payload;
      state.selectedDuration = selectedDuration;
    },
    setSelectedTimeRange: (state, action) => {
      const { selectedStartDate, selectedEndDate } = action.payload;
      const toIso = (value) =>
        value instanceof Date ? value.toISOString() : value;
      if (selectedStartDate !== undefined && selectedStartDate !== null) {
        state.selectedStartDate = toIso(selectedStartDate);
      }
      if (selectedEndDate !== undefined && selectedEndDate !== null) {
        state.selectedEndDate = toIso(selectedEndDate);
      }
    },
    setHasTimeSelected: (state, action) => {
      const { timeSelected } = action.payload;
      state.timeSelected = timeSelected;
    },
    setAdditionalComments: (state, action) => {
      const { additionalComments } = action.payload;
      state.additionalComments = additionalComments;
    },
    setRecurrenceRule: (state, action) => {
      const { recurrenceRule } = action.payload;
      state.recurrenceRule = recurrenceRule;
      state.selectedDuration = null;
      state.selectedStartDate = null;
      state.selectedEndDate = null;
      state.selectedTimeRangeForUIShow = null;
      state.numberOfsessions = null;
      state.regularServiceArrays = [];
      state.staffAvailabilityArrayForRegular = [];
    },
    setRecurrenceRuleSoft: (state, action) => {
      const { recurrenceRule } = action.payload;
      state.recurrenceRule = recurrenceRule;
    },
    setFrequencyShow: (state, action) => {
      const { frequencyShow } = action.payload;
      state.frequencyShow = frequencyShow;
    },
    setNumberOfSessions: (state, action) => {
      const { numberOfsessions } = action.payload;
      state.numberOfsessions = numberOfsessions;
    },
    setRegularServiceArrays: (state, action) => {
      const { regularServiceArrays } = action.payload;
      state.regularServiceArrays = regularServiceArrays;
    },
    setProfServiceArrays: (state, action) => {
      const { selectedProfServiceArray } = action.payload;
      const findIndex = state.selectedProfServiceArray.findIndex(
        (item) => item?.serviceId === selectedProfServiceArray?.serviceId
      );
      if (findIndex > -1) {
        state.selectedProfServiceArray[findIndex] = selectedProfServiceArray;
      } else {
        state.selectedProfServiceArray = [
          ...state.selectedProfServiceArray,
          selectedProfServiceArray,
        ];
      }
    },
    removeOneProfServiceArrays: (state, action) => {
      const { selectedProfServiceArray } = action.payload;
      state.selectedProfServiceArray = selectedProfServiceArray;
    },
    setPreferredDates: (state, action) => {
      const { preferredDates } = action.payload;
      state.preferredDates = preferredDates;
    },
    setSelectedSubOptionData: (state, action) => {
      const { subOptionData } = action.payload;
      state.subOptionData = subOptionData;
    },
    setProfessionalServiceAllFilesForUiShow: (state, action) => {
      state.professionalServiceAllFilesForUiShow = [
        ...state.professionalServiceAllFilesForUiShow,
        action.payload,
      ];
    },
    setDeleteProfessionalServiceAllFilesForUiShow: (state, action) => {
      state.professionalServiceAllFilesForUiShow = [...action.payload];
    },
    setDeleteProfessionalServiceAllFiles: (state, action) => {
      state.professionalServiceAllFiles = [...action.payload];
    },
    setDayNightOptionsForUi: (state, action) => {
      const { dayNightOptions } = action.payload;

      state.dayNightOptionsForUi = dayNightOptions;
    },
    setTimetimeRangeSelectionForDropDown: (state, action) => {
      const { timeRangeSelectionForDropDown } = action.payload;

      state.timeRangeSelectionForDropDown = timeRangeSelectionForDropDown;
    },

    setSelectedTimeRangeForUIShow: (state, action) => {
      state.selectedTimeRangeForUIShow = action.payload;
    },

    setClearOrder: (state, action) => {
      // state.customerList = [];
      // state.selectedCustomer = null;
      // state.selectedAddress = null;
      state.selectedTimeRangeForUIShow = null;
      state.professionalServiceAllFiles = [];
      state.professionalServiceAllFilesForUiShow = [];
      state.selectedCategoryOption = null;
      state.selectedDiscount = null;
      state.selectedService = null;
      state.selectedDuration = null;
      state.selectedStartDate = null;
      state.timeSelected = true;
      state.selectedEndDate = null;
      state.priceDetails = null;
      state.additionalComments = null;
      state.recurrenceRule = {};
      state.numberOfsessions = 0;
      state.regularServiceArrays = [];
      state.selectedProfServiceArray = [];
      state.profServiceArray = [];
      state.preferredDates = [];
      state.professionalServiceAllList = [];
      state.dayNightOptionsForUi = [];
      state.analysisArray = [];
      state.compalsaryMediaUpload = [];
      state.serviceTypes = [];
    },
    setClearAllOrderData: (state, action) => {
      state.selectedTimeRangeForUIShow = null;
      state.customerList = [];
      state.selectedCustomer = null;
      state.addressCheckedFromStore = false;
      state.selectedAddress = null;
      state.selectedCategory = null;
      state.selectedDiscount = null;
      state.selectedService = null;
      state.selectedDuration = null;
      state.selectedStartDate = null;
      state.selectedEndDate = null;
      state.priceDetails = null;
      state.additionalComments = null;
      state.recurrenceRule = {};
      state.numberOfsessions = 0;
      state.regularServiceArrays = [];
      state.hasStaffAvailable = false;
      state.frequencyShow = 0;
      state.subOptionData = null;
      state.selectedPreferredStaff = [];
      state.analysisArray = [];
      state.compalsaryMediaUpload = [];
      state.serviceTypes = [];
    },
    // this is for only when the user changes the type like from just once to daily so delete all the data except category
    setClearAllDataExceptCategory: (state, action) => {
      state.selectedTimeRangeForUIShow = null;
      state.customerList = [];
      state.selectedCustomer = null;
      // state.addressCheckedFromStore = false;
      state.selectedDiscount = null;
      state.selectedService = null;
      state.selectedDuration = null;
      state.selectedStartDate = null;
      state.selectedEndDate = null;
      state.priceDetails = null;
      state.additionalComments = null;
      state.recurrenceRule = {};
      state.numberOfsessions = 0;
      state.regularServiceArrays = [];
      state.hasStaffAvailable = false;
      state.frequencyShow = 0;
      state.subOptionData = null;
      state.selectedPreferredStaff = [];
      state.professionalServiceAllFiles = [];
      state.professionalServiceAllFilesForUiShow = [];
      state.selectedDiscount = null;
      state.selectedService = null;
      state.selectedDuration = null;
      state.selectedStartDate = null;
      state.timeSelected = true;
      state.selectedEndDate = null;
      state.priceDetails = null;
      state.additionalComments = null;
      state.recurrenceRule = {};
      state.numberOfsessions = 0;
      state.regularServiceArrays = [];
      state.selectedProfServiceArray = [];
      state.profServiceArray = [];
      state.preferredDates = [];
      state.professionalServiceAllList = [];
      state.dayNightOptionsForUi = [];
      state.compalsaryMediaUpload = [];
      state.serviceTypes = [];
    },

    setAnotherServiceAddEnable: (state, action) => {
      state.selectedDiscount = null;
      state.selectedService = null;
      state.selectedDuration = null;
      state.selectedStartDate = null;
      state.selectedEndDate = null;
      // state.profServiceArray = null
    },
    selectPreferredStaff: (state, action) => {
      state.selectedPreferredStaff = action.payload;
    },
    setAnalysisArray: (state, action) => {
      state.analysisArray = action.payload;
    },
    setCompalsaryMediaUpload: (state, action) => {
      state.compalsaryMediaUpload = action.payload;
    },
    setServiceTypes: (state, action) => {
      state.serviceTypes = state.serviceTypes
        ? [...state.serviceTypes, action.payload]
        : [action.payload];
    },
  },
  extraReducers: {
    [checkPrice.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [checkPrice.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;
      state.priceDetails = payload?.result;

      return state;
    },
    [checkPrice.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      return state;
    },
    [getStaffs.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [getStaffs.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;
      if (payload?.result?.length > 0) {
        state.hasStaffAvailable = true;
      }
      return state;
    },
    [getStaffs.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      state.hasStaffAvailable = false;
      return state;
    },
    [getStaffsGenReg.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [getStaffsGenReg.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;
      if (payload?.result?.data?.length > 0) {
        if (!state.selectedPreferredStaff?.length) {
          state.secondaryStaff = payload?.result?.data[0]?.user?._id ?? "";
        }
        state.hasStaffAvailable = true;
        state.staffAvailabilityArrayForRegular = payload?.result?.data;
      }
      return state;
    },
    [getStaffsGenReg.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      state.hasStaffAvailable = false;
      return state;
    },
    [checkDiscount.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [checkDiscount.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;

      const validation = validateDiscountCode(
        payload?.result,
        state.priceDetails?.subtotal
      );
      if (validation.valid) {
        state.selectedDiscount = normalizeDiscountResult(payload?.result);
      } else {
        toast.error(validation.message);
      }
      return state;
    },
    [checkDiscount.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      return state;
    },
    [checkDiscountNonLogin.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [checkDiscountNonLogin.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;

      const validation = validateDiscountCode(
        payload?.result,
        state.priceDetails?.subtotal
      );
      if (validation.valid) {
        state.selectedDiscount = normalizeDiscountResult(payload?.result);
      } else {
        toast.error(validation.message);
      }
      return state;
    },
    [checkDiscountNonLogin.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      return state;
    },
    [createNewOrder.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [createNewOrder.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;
      return state;
    },
    [createNewOrder.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      return state;
    },
    //
    [getAllServicedData.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [getAllServicedData.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;
      state.professionalServiceAllList = payload?.result;
      return state;
    },
    [getAllServicedData.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      return state;
    },
    [fileSelection.pending]: (state) => {
      state.isFetching = true;
      state.isError = false;
      return state;
    },
    [fileSelection.fulfilled]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = false;
      state.error = null;
      state.professionalServiceAllFiles = [
        ...state.professionalServiceAllFiles,
        { preSignedUrl: payload?.data?.result, serviceId: payload?.serviceId },
      ];
      return state;
    },
    [fileSelection.rejected]: (state, { payload }) => {
      state.isFetching = false;
      state.isError = true;
      state.error = payload;
      return state;
    },
  },
});

// this is for dispatch
export const {
  setSelectedOrder,
  setSelectCategory,
  setSelectCategoryOptions,
  setSelectedService,
  setSelectedDuration,
  setSelectedTimeRange,
  setHasTimeSelected,
  setAdditionalComments,
  setClearOrder,
  setRecurrenceRule,
  setFrequencyShow,
  setNumberOfSessions,
  setRegularServiceArrays,
  setClearAllOrderData,
  setClearAllDataExceptCategory,
  setProfServiceArrays,
  setAnotherServiceAddEnable,
  setPreferredDates,
  removeOneProfServiceArrays,
  selectPreferredStaff,
  setSelectAddress,
  setSelectedDiscount,
  setSelectedSubOptionData,
  setAddressCheckedFromStore,
  setProfessionalServiceAllFilesForUiShow,
  setDeleteProfessionalServiceAllFilesForUiShow,
  setDeleteProfessionalServiceAllFiles,
  setDayNightOptionsForUi,
  setAnalysisArray,
  setTimetimeRangeSelectionForDropDown,
  setSelectedTimeRangeForUIShow,
  setCompalsaryMediaUpload,
  setServiceTypes,
  setRecurrenceRuleSoft,
} = createOrderSlice.actions;

// this is for configureStore
export default createOrderSlice.reducer;
